import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/logo.jpg';
import { FaInstagram, FaArrowRight } from 'react-icons/fa';
import { SOCIAL_LINKS } from '../seoConfig';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [menuKey, setMenuKey] = useState(0);
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = isMenuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isMenuOpen]);

    const handleNavigation = (path) => {
        setIsMenuOpen(false);
        navigate(path);
        window.scrollTo(0, 0);
    };

    const closeMenu = () => setIsMenuOpen(false);

    const toggleMenu = () => {
        setIsMenuOpen((open) => {
            if (!open) setMenuKey((k) => k + 1);
            return !open;
        });
    };

    useEffect(() => {
        if (!isMenuOpen) return undefined;

        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeMenu();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isMenuOpen]);

    const navLinks = [
        { name: 'Stock', path: '/automoveis', num: '01' },
        { name: 'Sobre', path: '/sobre', num: '02' },
        { name: 'Contactos', path: '/contactos', num: '03' },
    ];

    const headerSolid = scrolled || isMenuOpen;

    return (
        <>
            <header
                className={`fixed top-0 left-0 w-full transition-all duration-500 ${
                    isMenuOpen ? 'z-[300]' : 'z-[100]'
                } ${
                    headerSolid
                        ? 'bg-black/95 backdrop-blur-md border-b border-white/[0.06]'
                        : 'bg-transparent'
                }`}
            >
                <div className="max-w-[1500px] mx-auto px-5 lg:px-12 h-[72px] flex items-center justify-between">
                    <button
                        type="button"
                        onClick={() => handleNavigation('/')}
                        className="flex items-center gap-3 group bg-transparent border-0 p-0 cursor-pointer relative z-[210]"
                    >
                        <div className="w-11 h-11 border border-white/10 flex items-center justify-center p-1.5 group-hover:border-red/50 transition-colors duration-300">
                            <img src={logo} alt="Auto João Pires" className="w-full h-full object-contain" />
                        </div>
                        <div className="hidden sm:block text-left">
                            <div className="font-display text-lg text-white tracking-widest leading-none">AJP</div>
                            <div className="text-[9px] font-bold uppercase tracking-[0.25em] text-white/40 mt-0.5">Auto João Pires</div>
                        </div>
                    </button>

                    <nav className="hidden lg:flex items-center gap-1">
                        {navLinks.map((link) => {
                            const isActive = location.pathname === link.path;
                            return (
                                <button
                                    key={link.path}
                                    type="button"
                                    onClick={() => handleNavigation(link.path)}
                                    className={`relative px-5 py-2 text-[11px] font-bold uppercase tracking-[0.2em] transition-colors duration-300 bg-transparent border-0 cursor-pointer ${
                                        isActive ? 'text-red' : 'text-white/50 hover:text-white'
                                    }`}
                                >
                                    <span className="text-red/40 mr-2 text-[9px]">{link.num}</span>
                                    {link.name}
                                    {isActive && (
                                        <span className="absolute bottom-0 left-5 right-5 h-[2px] bg-red" />
                                    )}
                                </button>
                            );
                        })}
                    </nav>

                    <div className="flex items-center gap-3 relative z-[210]">
                        <a
                            href={SOCIAL_LINKS.instagram}
                            target="_blank"
                            rel="noreferrer"
                            className="hidden md:flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/40 hover:text-red transition-colors"
                        >
                            <FaInstagram size={12} />
                            @autojoaopirespt
                        </a>

                        <button
                            type="button"
                            onClick={() => handleNavigation('/automoveis')}
                            className="hidden md:flex items-center gap-2 px-4 py-2 bg-red text-black text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors duration-300 border-0 cursor-pointer"
                        >
                            Stock
                            <FaArrowRight size={9} />
                        </button>

                        <button
                            type="button"
                            onClick={toggleMenu}
                            aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
                            aria-expanded={isMenuOpen}
                            className={`lg:hidden w-11 h-11 flex flex-col items-center justify-center gap-[5px] border transition-all duration-300 ${
                                isMenuOpen
                                    ? 'border-red text-red bg-red/10 ajp-burger-open'
                                    : 'border-white/15 text-white hover:border-red hover:text-red'
                            }`}
                        >
                            <span className="ajp-burger-line" />
                            <span className="ajp-burger-line" />
                            <span className="ajp-burger-line" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile menu overlay */}
            <div
                className={`fixed inset-0 z-[200] lg:hidden transition-opacity duration-500 ${
                    isMenuOpen ? 'opacity-100 visible pointer-events-auto' : 'opacity-0 invisible pointer-events-none'
                }`}
                aria-hidden={!isMenuOpen}
            >
                <button
                    type="button"
                    aria-label="Fechar menu"
                    onClick={() => setIsMenuOpen(false)}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm border-0 cursor-pointer"
                />

                <div
                    key={menuKey}
                    className={`absolute inset-x-0 top-[72px] bottom-0 bg-black overflow-hidden pointer-events-auto ${
                        isMenuOpen ? 'ajp-menu-curtain' : ''
                    }`}
                >
                    <div className="ajp-grain absolute inset-0 pointer-events-none" />

                    <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-red/[0.06] to-transparent pointer-events-none" />
                    <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red/40 to-transparent pointer-events-none" />

                    <div className="relative z-10 h-full flex flex-col px-6 sm:px-10 pt-8 pb-10">
                        <div className="mb-10 flex items-center justify-between gap-4">
                            <p className="section-tag !text-white/50">Navegação</p>
                            <span className="font-display text-6xl text-white/[0.04] leading-none select-none">AJP</span>
                        </div>

                        <nav className="flex-1 flex flex-col justify-center gap-1 -mt-8">
                            {navLinks.map((link, index) => {
                                const isActive = location.pathname === link.path;
                                return (
                                    <button
                                        key={link.path}
                                        type="button"
                                        onClick={() => handleNavigation(link.path)}
                                        className={`ajp-menu-link group relative flex items-end gap-5 sm:gap-8 py-5 sm:py-6 text-left bg-transparent border-0 border-b border-white/[0.06] cursor-pointer w-full overflow-hidden ${
                                            isActive ? 'text-red' : 'text-white'
                                        }`}
                                        style={{ animationDelay: isMenuOpen ? `${120 + index * 90}ms` : '0ms' }}
                                    >
                                        <span
                                            className={`font-display text-4xl sm:text-5xl transition-colors duration-300 ${
                                                isActive ? 'text-red' : 'text-red/25 group-hover:text-red'
                                            }`}
                                        >
                                            {link.num}
                                        </span>
                                        <span className="flex-1">
                                            <span className="block font-display text-[clamp(2.5rem,10vw,4.5rem)] tracking-widest leading-none group-hover:translate-x-2 transition-transform duration-500 ease-out">
                                                {link.name}
                                            </span>
                                            <span
                                                className={`block h-[2px] mt-3 origin-left transition-all duration-500 ${
                                                    isActive
                                                        ? 'w-full bg-red'
                                                        : 'w-0 bg-red group-hover:w-full'
                                                }`}
                                            />
                                        </span>
                                        <FaArrowRight
                                            size={14}
                                            className="mb-2 opacity-0 -translate-x-3 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-red shrink-0"
                                        />
                                    </button>
                                );
                            })}
                        </nav>

                        <div
                            className="ajp-menu-footer space-y-5 pt-8 border-t border-white/[0.06]"
                            style={{ animationDelay: isMenuOpen ? '480ms' : '0ms' }}
                        >
                            <a
                                href={SOCIAL_LINKS.instagram}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-3 text-white/50 hover:text-red text-[11px] font-bold uppercase tracking-[0.2em] transition-colors"
                            >
                                <FaInstagram size={14} />
                                @autojoaopirespt
                            </a>

                            <button
                                type="button"
                                onClick={() => handleNavigation('/automoveis')}
                                className="btn-primary w-full justify-center sm:w-auto"
                            >
                                Ver Stock <FaArrowRight size={10} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Header;
